<template>
  <div class="">
    <!-- Header and Add Button -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
     <div class="flex items-center justify-center gap-1">
        <h2 class="text-xl font-semibold text-gray-800">List of web banners</h2>
        <a href="https://www.notion.so/Banners-765409d6e99f4e61b22e163b4237fc9f" target="_blank">
          <span class="material-icons text-gray-400 hover:text-gray-600 mt-1" style="font-size: 20px" v-popover:tooltip="'Documentation'">info</span>
        </a>
      </div>
      <button
        @click="addingNewItem"
        class="flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white font-semibold px-3 py-2 rounded-lg shadow transition"
      >
        <span class="material-icons text-base">add</span>
        Add Banner
      </button>
    </div>
    <!-- Filter Bar -->
    <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
      <div class="flex bg-gray-200 rounded-lg">
        <button
          :class="[
            'px-4 py-2 font-medium focus:outline-none transition select-none',
            search.status === '0' ? 'bg-white text-indigo-700 font-semibold shadow-md rounded-lg z-10' : 'text-gray-600 hover:bg-gray-200',
          ]"
          @click="
            search.status = '0';
            searchData();
          "
        >
          All
        </button>
        <button
          :class="[
            'px-4 py-2 font-medium focus:outline-none transition select-none',
            search.status === '1' ? 'bg-white text-indigo-700 font-semibold shadow-md rounded-lg z-10' : 'text-gray-600 hover:bg-gray-200',
          ]"
          @click="
            search.status = '1';
            searchData();
          "
        >
          Active
        </button>
        <button
          :class="[
            'px-4 py-2 font-medium focus:outline-none transition select-none',
            search.status === '2' ? 'bg-white text-indigo-700 font-semibold shadow-md rounded-lg z-10' : 'text-gray-600 hover:bg-gray-200',
          ]"
          @click="
            search.status = '2';
            searchData();
          "
        >
          Inactive
        </button>
      </div>
      <!--  Search and Sort Controls -->
      <div class="flex items-center gap-2">
        <vs-select v-model="search.language" @change="searchData" class="w-28" :color="'#6366f1'">
          <vs-select-item value="all" text="Language" disabled />
          <vs-select-item v-for="lang in languages" :key="lang.culture" :value="lang.culture" :text="lang.culture" />
        </vs-select>
        <vs-select v-model="search.sort" @change="searchData" class="w-28" :color="'#6366f1'">
          <vs-select-item value="ASC" text="Ascending" />
          <vs-select-item value="DESC" text="Descending" />
        </vs-select>
        <vs-select class="w-28" v-model="search.isToday" @change="searchData()">
          <vs-select-item value="false" text="All days" />
          <vs-select-item value="true" text="Today only" />
        </vs-select>
        <button
          @click="sortPositions()"
          class="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition"
        >
          <span class="material-icons text-base">sort</span> Sort
        </button>
        <button
          @click="resetData()"
          class="flex items-center gap-1 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium transition"
        >
          <span class="material-icons text-base">refresh</span> Reset
        </button>
      </div>
    </div>
    <!-- Table -->
    <div class="overflow-x-auto rounded-xl border border-gray-200 bg-white pb-2">
      <table class="min-w-full bg-white rounded-xl">
        <thead class="border-b">
          <tr>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
            <th class="px-4 py-3"></th>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Title</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase">Details</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase">Position</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase">Language</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase">More Info</th>
            <th class="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, idx) in all" :key="item._id" class="border-b hover:bg-gray-100 transition">
            <td class="px-4 py-1">{{ idx + 1 }}</td>
            <td class="">
              <div class="flex items-center gap-1.5">
                <img :src="item.image || require('@/assets/images/No-image.png')" class="rounded-md object-cover w-20 h-16" />
              </div>
            </td>
            <td class="px-4 py-1 font-medium text-gray-700">
              <div class="flex flex-col gap-1 flex-wrap">
                {{ item.title.length > 50 ? item.title.slice(0, 50) + "..." : item.title }}
                <span class="text-gray-500 text-sm">Start: {{ formatDate(item.startDate) }}</span>
                <span class="text-gray-500 text-sm">End: {{ formatDate(item.endDate) }}</span>
              </div>
            </td>
            <td class="px-4 py-1 text-center">
              <button @click.stop="viewBannerDetails(item)" class="focus:outline-none">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </button>
            </td>
            <td class="px-4 py-1 text-center">
              <div class="flex flex-col items-center gap-1">
                <button
                  class="flex items-center justify-center border-2 border-gray-100 rounded-md w-6 h-6 disabled:opacity-50"
                  :disabled="parseInt(item.position) === 1"
                  @click.stop="updatePosition(item._id, search.sort === 'ASC' ? 'up' : 'down')"
                >
                  <svg
                    :class="parseInt(item.position) === 1 ? 'text-gray-200' : 'text-gray-400'"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    class="w-10 h-10"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 15l-7-7-7 7" />
                  </svg>
                </button>
                <span class="font-semibold text-gray-700">{{ item.position }}</span>
                <button
                  class="flex items-center justify-center border-2 border-gray-100 rounded-md w-6 h-6 disabled:opacity-50"
                  :disabled="parseInt(item.position) === all.length"
                  @click.stop="updatePosition(item._id, search.sort === 'ASC' ? 'down' : 'up')"
                >
                  <svg
                    :class="parseInt(item.position) === all.length ? 'text-gray-200' : 'text-gray-400'"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    class="w-10 h-10"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </td>
            <td
              class="px-4 py-1 text-center"
              :title="item.missingLanguages.length > 0 ? `Missing: ${item.missingLanguages.join(', ')}` : 'All languages available'"
            >
              <div class="flex items-center justify-center gap-2">
                <span class="text-gray-500 text-lg">{{ item.language }}</span>
                <span
                  v-if="item.languageCount && item.missingLanguages && item.language === 'EN'"
                  :class="[
                    'inline-flex items-center px-2 py-1 rounded-full font-semibold cursor-pointer',
                    item.languageCount - item.missingLanguages.length !== 11 ? 'bg-red-900 text-white' : 'bg-blue-900 text-white',
                  ]"
                >
                  {{ item.languageCount - item.missingLanguages.length }}/{{ item.languageCount }}
                </span>
                <span v-if="item.language === 'EN'" class="material-icons text-gray-400 cursor-pointer" style="font-size: 20px">info</span>
              </div>
            </td>
            <td class="px-4 py-1 text-center">
              <div class="flex w-full items-center justify-between gap-5 mb-2">
                Status
                <span
                  :class="item.status ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'"
                  class="inline-block px-3 py-1 rounded-full font-semibold"
                >
                  {{ item.status ? "Active" : "Inactive" }}
                </span>
              </div>
              <div class="flex w-full items-center justify-between gap-5 mb-2">
                Repeatable
                <span
                  :class="item.isRepeat ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'"
                  class="inline-block px-3 py-1 rounded-full font-semibold"
                >
                  {{ item.isRepeat ? "YES" : "NO" }}
                </span>
              </div>
            </td>

            <td class="px-4 py-1">
              <vs-dropdown>
                <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
                <vs-dropdown-menu>
                  <vs-dropdown-item @click="editingItem(item)"> <vs-icon icon="create" class="align-right" /> Edit </vs-dropdown-item>
                  <vs-dropdown-item
                    @click="
                      deleteBannerPopup = true;
                      itemToDelete = item;
                    "
                  >
                    <vs-icon icon="delete_sweep" /> Delete
                  </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>
            </td>
          </tr>
          <tr v-if="!all || all.length === 0">
            <td colspan="12" class="text-center h-full py-6 text-gray-400">No banners found.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <vs-popup :title="isNew ? 'Add New Banner' : clone ? 'Duplicate Banner' : 'Edit Banner'" :active.sync="defaultalert">
      <div class="flex flex-col gap-1 px-2">
        <!-- language filter -->
        <div class="flex justify-between gap-2 w-full mb-2">
          <div class="w-1/4" v-if="!isNew">
            <label class="block text-sm font-semibold mb-2">Language</label>
            <vs-select
              v-model="banner.language"
              @change="fileterusingLanguage($event, banner)"
              class="w-full"
              :color="'#6366f1'"
              :disabled="showLoader"
            >
              <vs-select-item value="" text="Select Language" disabled />
              <vs-select-item v-for="lang in languages" :key="lang.culture" :value="lang.culture" :text="lang.culture" />
            </vs-select>
          </div>
          <div v-if="!isNew && banner.language === 'EN'" :title="'Toggle to update existing translations'">
            <!-- toggle pill to update existing translations -->
            <label class="block text-sm font-semibold mb-2">Update Translations</label>
            <toggle-pill v-model="banner.translate" :disabled="showLoader" />
          </div>
        </div>
        <!-- title -->
        <label class="block text-sm font-semibold">Title*</label>
        <vs-input v-model="banner.title" placeholder="Keep short and simple" class="w-full" required :disabled="showLoader" />
        <!-- description -->
        <label class="block text-sm font-semibold">Description*</label>
        <vs-textarea v-model="banner.description" class="w-full" required :disabled="showLoader" />
        <div class="flex items-center justify-between gap-2">
          <div class="flex flex-col gap-1 w-full">
            <!-- link -->
            <label class="block text-sm font-semibold">Link</label>
            <vs-input v-model="banner.link" class="w-full" required :disabled="showLoader" />
          </div>
          <div class="flex flex-col gap-1 w-full">
            <!-- position -->
            <label class="block text-sm font-semibold">Position*</label>
            <vs-input v-model="banner.position" type="number" class="w-full" required :disabled="showLoader" />
          </div>
        </div>
        <!-- checkbioxes -->
        <div class="flex items-center gap-2 justify-center">
          <vs-checkbox v-model="banner.isRepeat">Repeatable?</vs-checkbox>
          <vs-checkbox v-model="banner.status">Status (Active / Inactive)</vs-checkbox>
        </div>
        <!-- image -->
        <div class="flex-1">
          <div class="flex justify-between items-start">
            <label class="block text-sm font-semibold">Image</label>
            <button
              v-if="displayImage"
              type="button"
              color="danger"
              class="bg-red-500 p-1 h-6 rounded-md hover:bg-red-700 transition text-white"
              @click="clearImage"
              :disabled="showLoader"
            >
              Clear
            </button>
          </div>
          <div
            class="flex flex-col items-center justify-center border-2 border-dashed rounded-md cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
            style="width: 100%; height: 140px; min-height: 140px"
            @dragover.prevent
            @dragenter.prevent
            @drop.prevent="onDropImage"
            @click="triggerFileInput"
          >
            <img v-if="displayImage" :src="displayImage" class="rounded-md object-cover border" style="width: 200px; max-height: 140px" />
            <div v-else class="flex flex-col items-center justify-center w-full h-full text-gray-400">
              <span class="material-icons" style="font-size: 32px">cloud_upload</span>
              <span>Drag & Drop or Click to Upload</span>
            </div>
            <input
              type="file"
              ref="fileInput"
              class="hidden"
              @change="onImageChange"
              accept="image/jpeg,image/jpg,image/webp"
              :disabled="showLoader"
            />
          </div>
          <br />
          (Try to use an image with at least 1000px height (2560x1000 is tested and works))
        </div>

        <!-- start and end date -->
        <div class="flex w-full mt-4 gap-2">
          <div class="flex flex-col gap-1">
            <label>Start Date*</label>
            <datepicker placeholder="Start Date" v-model="banner.startDate" format="yyyy-MM-dd" input-class="border" class="zIndexClass">
            </datepicker>
          </div>
          <div class="flex flex-col gap-1">
            <label>End Date*</label>
            <datepicker placeholder="End Date" v-model="banner.endDate" format="yyyy-MM-dd" input-class="border" class="form-group"> </datepicker>
          </div>
        </div>

        <div class="pt-2" v-if="isNew && banner.language === 'EN'">
          <label class="block text-sm font-semibold mb-2">Choose Languages to Translate</label>
          <div class="grid grid-cols-7 gap-2">
            <vs-checkbox v-model="selectAllLang" @change="toggleSelectAll" :disabled="showLoader" class="z-0">All</vs-checkbox>
            <div v-for="lang in languages" :key="lang.culture">
              <vs-checkbox
                :value="banner.languagesToTranslate.includes(lang.culture)"
                @change="toggleLanguage(lang.culture)"
                class="z-0"
                :disabled="lang.culture === 'EN' || showLoader"
              >
                {{ lang.culture }}
              </vs-checkbox>
            </div>
          </div>
        </div>
        <!-- add missing languages -->
        <div v-if="!isNew && banner.language === 'EN' && banner.missingLanguages && banner.missingLanguages.length > 0">
          <label class="block text-sm font-semibold mb-2">Add missing translations</label>
          <vs-checkbox v-model="banner.translateMissing" :disabled="showLoader">Add missing translations</vs-checkbox>
          <div v-if="banner.translateMissing" class="grid grid-cols-7 gap-2 mt-3">
            <vs-checkbox v-model="selectAllEdit" @change="toggleSelectAllEdit" :disabled="showLoader" class="z-0">All</vs-checkbox>
            <div v-for="lang in banner.missingLanguages" :key="lang">
              <vs-checkbox
                :value="banner.languagesToTranslate.includes(lang) && lang !== 'EN'"
                :disabled="lang === 'EN' || showLoader"
                @change="toggleLanguage(lang)"
                class="z-0"
              >
                {{ lang }}
              </vs-checkbox>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2 w-full mt-2">
          <vs-button icon="save" class="w-full" style="z-index: 1" @click="btnAddBanner" v-if="!banner.translateMissing">
            {{ banner.languagesToTranslate.length > 1 ? "Save and Translate" : isNew ? "Save English" : "Update one" }}
          </vs-button>
          <!-- <vs-button class="w-full" icon="save" style="z-index: 1" v-if="isNew" @click="createAndTranslate">Save and Translate</vs-button> -->
          <vs-button
            class="w-full"
            icon="save"
            style="z-index: 1"
            v-if="!showLoader && !isNew && !clone && banner.language === 'EN'"
            @click="updateAndTranslate"
          >
            {{ banner.translateMissing ? "Update and Translate Missing" : banner.translate ? "Update and Translate" : "Update all" }}</vs-button
          >
        </div>
      </div>
    </vs-popup>

    <vs-popup title="Delete Banner" :active.sync="deleteBannerPopup">
      <p class="mb-4 text-lg text-gray-700">Are you sure you want to delete this banner?</p>
      <div class="flex justify-end gap-2">
        <vs-button color="warning" @click="deleteBanner">Delete</vs-button>
        <vs-button color="primary" @click="deleteBannerPopup = false">Cancel</vs-button>
      </div>
    </vs-popup>

    <!-- view description -->
    <vs-popup title="View Details" :active.sync="viewDetails">
      <div class="flex flex-col gap-2">
        <h6>Images:</h6>
        <div v-for="img in detail.images">
          <img v-if="img" :src="img || require('@/assets/images/No-image.png')" />
        </div>

        <h6>Description :</h6>
        <p>{{ detail.description }}</p>
        <div class="flex items-center gap-1" v-if="detail.link">
          <h6>Link :</h6>
          <a>{{ detail.link }}</a>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import api from "@/rest.js";
import { API } from "@/common/constants";
import TogglePill from "../../components/Common/TogglePill.vue";
import Datepicker from "vuejs-datepicker";
import Vue from "vue";
import VPopover from "vue-js-popover";
import moment from "moment";

Vue.use(VPopover, { tooltip: true });

export default {
  name: "mBanner",
  components: {
    Datepicker,
    TogglePill,
  },
  data: () => ({
    title: "Banner",
    defaultalert: false,
    viewDetails: false,
    deleteBannerPopup: false,
    isNew: true,
    clone: false,
    search: {
      sort: "ASC",
      language: "EN",
      status: "0",
      isToday: "false",
    },
    displayImage: "",
    fileName: "",
    banner: {
      status: true,
      isRepeat: false,
      language: "EN",
      position: "",
      title: "",
      image: "",
      translate: false,
      languagesToTranslate: [],
      translateMissing: false,
    },
    selectAllLang: false,
    selectAllEdit: false,
    itemToDelete: "",
    itemToUpdate: "",
    all: [],
    languages: [],
    detail: {},
    fileInputs: {},
    showLoader: false,
  }),
  methods: {
    toggleSelectAll() {
      if (this.selectAllLang) {
        this.banner.languagesToTranslate = this.languages.map((lang) => lang.culture);
      } else {
        this.banner.languagesToTranslate = ["EN"];
      }
    },

    toggleSelectAllEdit() {
      if (this.selectAllEdit) {
        this.banner.languagesToTranslate = this.banner.missingLanguages;
      } else {
        this.banner.languagesToTranslate = ["EN"];
      }
    },

    toggleLanguage(culture) {
      const selectedLanguages = this.banner.languagesToTranslate;

      if (selectedLanguages.includes(culture)) {
        this.banner.languagesToTranslate = selectedLanguages.filter((lang) => lang !== culture);
      } else {
        this.banner.languagesToTranslate = [...selectedLanguages, culture];
      }

      const allLanguagesSelected = this.languages.every((lang) => this.banner.languagesToTranslate.includes(lang.culture));
      this.selectAllLang = allLanguagesSelected;
      if (this.selectAllEdit) {
        const allLanguagesSelected = this.banner.missingLanguages.every((lang) => this.banner.languagesToTranslate.includes(lang));
        this.selectAllEdit = allLanguagesSelected;
      }
    },

    addingNewItem() {
      this.clearImage();
      this.$data.isNew = true;
      this.$data.clone = false;
      this.$data.banner = {
        status: true,
        isRepeat: false,
        language: "EN",
        position: "",
        title: "",
        image: "",
        translate: false,
        languagesToTranslate: ["EN"],
        translateMissing: false,
      };
      this.$data.selectAllLang = false;
      this.$data.selectAllEdit = false;
      this.$data.displayImage = "";
      this.$data.defaultalert = true;
    },

    editingItem(i, data) {
      this.$data.banner = Object.assign(this.$data.banner, i);
      this.$data.isNew = false;
      this.$data.banner.languagesToTranslate = ["EN"];
      this.$data.selectAllLang = false;
      this.$data.selectAllEdit = false;
      this.$data.banner.translateMissing = false;
      this.$data.displayImage = i.image || "";
      this.$data.clone = data == "clone" ? true : false;
      this.$data.defaultalert = true;
    },

    onImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.banner.image = file;
        this.displayImage = URL.createObjectURL(file);
      }
    },

    clearImage() {
      this.displayImage = "";
      this.banner.image = "";
      if (this.$refs.fileInput) this.$refs.fileInput.value = null;
    },

    triggerFileInput() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },

    onDropImage(e) {
      const files = e.dataTransfer.files;
      if (files && files[0]) {
        this.onImageChange({ target: { files } });
      }
    },

    formatDate(date) {
      return moment(date).format("YYYY-MM-DD");
    },

    async resetData() {
      this.$data.search = {
        sort: "ASC",
        language: "EN",
        status: "0",
        isToday: "false",
      };
      await this.searchData();
    },

    viewBannerDetails(i) {
      this.$data.detail.description = i.description;
      this.$data.detail.link = i.link;
      this.$data.detail.image = i.image;
      this.$data.viewDetails = true;
    },

    async uploadImage(data) {
      const file = data.target.files[0];
      if (!file) return;

      if (file.type !== "image/jpeg" && file.type !== "image/jpg" && file.type !== "image/webp") {
        this.$vs.notify({
          title: "Uploading Error",
          text: "Only JPEG, JPG, or WEBP image formats are supported",
          color: "danger",
          position: "top-right",
        });
        throw new Error("Unsupported file format");
      }

      if (file.size > 500000) {
        this.$vs.notify({
          title: "Uploading Error",
          text: "File size should be less than 500kb",
          color: "danger",
          position: "top-right",
        });
        throw new Error("File size exceeds limit");
      }

      let formdata = new FormData();

      formdata.append("file", file);
      formdata.append("banner", this.$data.banner.title);

      this.$vs.loading();
      try {
        const response = await api.post(API.UPLOAD_IMAGE, formdata, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.status === 200) {
          this.$data.displayImage = response.data.files[0].url;

          this.$vs.notify({
            text: "File Uploaded",
            color: "success",
            position: "top-right",
          });
          this.$emit("input", response);
        }
      } catch (error) {
        this.$vs.notify({
          text: "File Uploading Failed",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
        if (this.$refs.fileInput) this.$refs.fileInput.value = null;
      }
    },

    validator(item) {
      const fields = ["title", "description", "position", "startDate", "endDate"];
      for (let field of fields) {
        if (!item[field] || item[field].toString().trim() === "") {
          this.$vs.notify({
            title: "Validation Error",
            text: `Please fill the ${field} field.`,
            color: "danger",
            position: "top-right",
          });
          return false;
        }
      }

      if (!this.displayImage) {
        this.$vs.notify({
          title: "Validation Error",
          text: "Please upload image.",
          color: "danger",
          position: "top-right",
        });
        return false;
      }

      return true;
    },

    async btnAddBanner() {
      if (this.banner.languagesToTranslate.length > 1) return await this.createAndTranslate();

      const isValid = this.validator(this.$data.banner);
      if (!isValid) return;
      this.$vs.loading();
      try {
        if (this.$data.banner.image instanceof File) await this.uploadImage({ target: { files: [this.$data.banner.image] } });

        let apiToCall = API.INSERT_WEB_BANNER;
        let data = this.$data.banner;
        data.image = this.$data.displayImage;

        let r;

        if (this.$data.isNew) r = await api.post(apiToCall, data);
        else {
          apiToCall = API.UPDATE_WEB_BANNER;
          r = await api.put(`${apiToCall}/${data._id}`, data);
        }

        if (r.status === 200) {
          this.$vs.loading.close();
          this.$vs.notify({
            text: "Saved Successfully",
            color: "success",
            position: "top-right",
          });
          this.$data.defaultalert = false;
          await this.searchData();
        }
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          text: "Error occurred while saving the banner",
          color: "danger",
          position: "top-right",
        });
        throw error;
      } finally {
        this.$vs.loading.close();
      }
    },

    async createAndTranslate() {
      if (!this.isNew && this.banner.translateMissing) return await this.updateAndTranslate();

      const isValid = this.validator(this.$data.banner);
      if (!isValid) return;
      this.$vs.loading();
      try {
        if (this.$data.banner.image instanceof File) await this.uploadImage({ target: { files: [this.$data.banner.image] } });

        let data = this.$data.banner;
        data.image = this.$data.displayImage;

        this.$data.showLoader = true;
        this.$data.defaultalert = false;

        const res1 = await api.post(API.C_AND_T_WEB_BANNER, data);

        this.$vs.loading.close();
        if (res1.status == 200) {
          this.$vs.notify({
            text: "Saved Successfully",
            color: "success",
            position: "top-right",
          });

          await this.searchData();
          this.$data.showLoader = false;
        }
      } catch (error) {
        this.$data.showLoader = false;
        this.$vs.notify({
          title: "Error",
          text: r.data.error,
          color: "danger",
          position: "top-right",
        });
        throw error;
      }
    },

    async updateAndTranslate() {
      const isValid = this.validator(this.$data.banner);
      if (!isValid) return;
      this.$vs.loading();
      try {
        if (this.$data.banner.image instanceof File) await this.uploadImage({ target: { files: [this.$data.banner.image] } });

        let data = this.$data.banner;
        data.image = this.$data.displayImage;
        data.translate = this.$data.banner.translate;

        this.$data.showLoader = true;
        this.$data.defaultalert = false;

        const r = await api.put(`${API.U_AND_T_WEB_BANNER}/${data._id}`, data);
        if (r.status == 200) {
          this.$vs.notify({
            text: "Updated Successfully",
            color: "success",
            position: "top-right",
          });

          await this.searchData();
          this.$data.showLoader = false;
          this.$data.defaultalert = false;
        }
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          text: " Error occurred while updating the banner",
          color: "danger",
          position: "top-right",
        });
        this.$data.showLoader = false;
        throw error;
      } finally {
        this.$vs.loading.close();
      }
    },

    async deleteBanner() {
      this.$vs.loading();

      try {
        const r = await api.delete(`${API.DELETE_WEB_BANNER}/${this.$data.itemToDelete._id}`);

        if (r.status === 200) {
          this.$vs.notify({
            text: "Deleted Successfully",
            color: "success",
            position: "top-right",
          });
          this.deleteBannerPopup = false;
          await this.searchData();
        }
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          text: "Error occurred while deleting the banner",
          color: "danger",
          position: "top-right",
        });
        this.$data.showLoader = false;
        throw error;
      } finally {
        this.$vs.loading.close();
      }
    },

    async updatePosition(id, type) {
      this.$vs.loading();
      try {
        const r = await api.put(`${API.UPDATE_WEB_BANNER_POSITION}/${id}?direction=${type}`, {});
        if (r.status === 200) {
          this.$vs.notify({
            text: "Position Updated Successfully",
            color: "success",
            position: "top-right",
          });
          this.searchData();
        }
      } catch (e) {
        this.$vs.notify({
          text: e.response.data.error || "Error updating position",
          color: "danger",
          position: "top-right",
        });
        throw e;
      } finally {
        this.$vs.loading.close();
      }
    },

    async sortPositions() {
      this.$vs.loading();
      try {
        const r = await api.put(`${API.SORT_WEB_BANNER_POSITION}?language=${this.$data.search.language}`);
        if (r.status === 200) {
          this.$vs.notify({
            text: "Sorted Successfully",
            color: "success",
            position: "top-right",
          });
          this.searchData();
        } else {
          this.$vs.notify({
            title: "Error",
            text: r.data.error,
            color: "danger",
            position: "top-right",
          });
        }
      } catch (e) {
        this.$vs.notify({
          text: "Error Sorting",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async fileterusingLanguage(event, item) {
      this.$vs.loading();
      try {
        if (event) {
          let id = item.ref || item._id;

          const r = await api.get(`${API.GET_SINGLE_WEB_BANNER}/${id}?language=${event}`);
          if (r.status === 200) {
            this.$data.banner = Object.assign({}, r.data);
            await this.searchData();
          }
        }
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          text: "Error occurred while fetching the banner for the selected language",
          color: "danger",
          position: "top-right",
        });
        this.$data.showLoader = false;
        throw error;
      } finally {
        this.$vs.loading.close();
      }
    },

    async searchData() {
      this.$vs.loading();
      try {
        const r = await api.get(
          `${API.GET_WEB_BANNER}?sort=${this.$data.search.sort}&language=${this.$data.search.language}&status=${this.$data.search.status}&isToday=${this.$data.search.isToday}`
        );
        for (const item of r.data) {
          item.tooltipContent =
            item.missingLanguages.length > 0 ? "Missing Languages: " + item.missingLanguages.join(", ") : "All Languages are translated";
        }
        this.$data.all = r.data;
        if (!r.status) {
          this.$vs.notify({
            title: "Error",
            text: r.data.error,
            color: "danger",
            position: "top-right",
          });
        }
      } catch (error) {
        this.$vs.notify({
          title: "Error",
          text: "Failed to fetch banners",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async fetchAllLanguages() {
      this.$vs.loading();
      try {
        const r = await api.get(API.GET_LANG, {});
        this.languages = r.data;
      } catch (error) {
        this.$vs.notify({
          text: "Error fetching languages",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },
  },
  async created() {
    await Promise.all([this.searchData(), this.fetchAllLanguages()]);
  },
};
</script>

<style scoped>
.material-icons {
  font-family: "Material Icons";
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-smoothing: antialiased;
}
</style>
